import React, { useRef, useState } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';
import 'swiper/css/navigation';
import { Navigation } from 'swiper/modules';
import Image from 'next/image';
import { useRouter } from 'next/navigation';
import { createSlug } from '@/app/utils/createSlug';

export interface UpComingDrama {
    d_id: string;
    d_logo: string;
    d_name: string;
    description: string;
    gradient: string;
    totl_episodes: string;
    unlock_count: string;
}

interface ImageSliderProps {
    data: UpComingDrama[];
    heading: string;
}


const ImageSlider: React.FC<ImageSliderProps> = ({ data, heading }) => {
    console.log(data)
    //eslint-disable-next-line @typescript-eslint/no-explicit-any
    const swiperRef = useRef<any>(null);
    const [isBeginning, setIsBeginning] = useState(true);
    const [isEnd, setIsEnd] = useState(false);
    const [hasInvisibleSlides, setHasInvisibleSlides] = useState<boolean>(() => {
        // Initial guess before Swiper init: based on viewport & breakpoints
        if (typeof window === 'undefined') return data.length > 3; // default guess on SSR
        const w = window.innerWidth;
        const visible = w < 480 ? 2 : 3;
        return data.length > visible;
    });

    const router = useRouter();
    //eslint-disable-next-line @typescript-eslint/no-explicit-any
    const handleSlideChange = (swiper: any) => {
        console.log(swiper)
        setIsBeginning(swiper.isBeginning);
        setIsEnd(swiper.isEnd);
    };
    //eslint-disable-next-line @typescript-eslint/no-explicit-any
    const handleSwiperInit = (swiper: any) => {
        const totalSlides = swiper.slides.length;
        const visibleSlides = typeof swiper.slidesPerViewDynamic === 'function'
            ? swiper.slidesPerViewDynamic()
            : (typeof swiper.params?.slidesPerView === 'number' ? swiper.params.slidesPerView : 1);
        setHasInvisibleSlides(totalSlides > visibleSlides);
        setIsBeginning(swiper.isBeginning);
        setIsEnd(swiper.isEnd);
    };

    // Recompute hasInvisibleSlides on resize or data change
    React.useEffect(() => {
        const recompute = () => {
            const swiper = swiperRef.current?.swiper;
            if (swiper) {
                const total = swiper.slides.length;
                const visible = typeof swiper.slidesPerViewDynamic === 'function'
                    ? swiper.slidesPerViewDynamic()
                    : (typeof swiper.params?.slidesPerView === 'number' ? swiper.params.slidesPerView : 1);
                setHasInvisibleSlides(total > visible);
                setIsBeginning(swiper.isBeginning);
                setIsEnd(swiper.isEnd);
            } else {
                const w = typeof window !== 'undefined' ? window.innerWidth : 1024;
                const visible = w < 480 ? 2 : 3;
                setHasInvisibleSlides(data.length > visible);
            }
        };
        recompute();
        if (typeof window !== 'undefined') {
            window.addEventListener('resize', recompute);
            return () => window.removeEventListener('resize', recompute);
        }
    }, [data.length]);

    const handleClick = (name: string, id: string) => {
        router.push(`/drama/${createSlug(name)}-${id}/?episode=0`);
    }


    const sliderContainerStyle: React.CSSProperties = {
        position: 'relative',
        padding: '20px 0',
        // maxWidth: '1200px',
        width: '100%',
        margin: '0 auto',
    };

    const headingContainerStyle: React.CSSProperties = {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '20px',
    };

    const headingStyle: React.CSSProperties = {
        color: '#fff',
        margin: 0,
        font: '500 24px / 1.4 Inter, sans-serif',
    };

    const navButtonsStyle: React.CSSProperties = {
        display: 'flex',
        gap: '10px',
    };

    const navButtonStyle: React.CSSProperties = {
        backgroundColor: '#444444',
        border: 'none',
        borderRadius: '50%',
        width: '40px',
        height: '40px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        cursor: 'pointer',
        color: '#fff',
        fontSize: '20px',
    };

    const prevButtonStyle: React.CSSProperties = {
        ...navButtonStyle,
        backgroundColor: isBeginning ? '#888' : '#444', // override
        cursor: isBeginning ? 'not-allowed' : 'pointer',
        opacity: isBeginning ? 0.5 : 1,
    };

    const nextButtonStyle: React.CSSProperties = {
        ...navButtonStyle,
        backgroundColor: isEnd || !hasInvisibleSlides ? '#888' : '#444',
        cursor: isEnd || !hasInvisibleSlides ? 'not-allowed' : 'pointer',
        opacity: isEnd || !hasInvisibleSlides ? 0.5 : 1,
    };

    const slideStyle: React.CSSProperties = {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        cursor: 'pointer',
        aspectRatio: '9 / 16',
    };

    const imageStyle: React.CSSProperties = {
        borderRadius: '8px',
        objectFit: 'cover',
        height: '100%',
        background:'2f2f2f'
    };

    return (
        <div style={sliderContainerStyle}>
            <div style={headingContainerStyle}>
                <h2 style={headingStyle}>{heading}</h2>
                <div style={navButtonsStyle}>
                    <button
                        style={prevButtonStyle}
                        onClick={() => swiperRef.current?.swiper.slidePrev()}
                        aria-label="Previous slide"
                        disabled={isBeginning}
                    >
                        &lt;
                    </button>
                    <button
                        style={nextButtonStyle}
                        onClick={() => swiperRef.current?.swiper.slideNext()}
                        aria-label="Next slide"
                        disabled={isEnd || !hasInvisibleSlides}
                    >
                        &gt;
                    </button>
                </div>
            </div>

            <Swiper
                ref={swiperRef}
                spaceBetween={20}
                slidesPerView={1}
                onSlideChange={handleSlideChange}
                onSwiper={handleSwiperInit}
                breakpoints={{
                    0: {
                        slidesPerView: 2,
                    },
                    480: {
                        slidesPerView: 3,
                    },
                    640: {
                        slidesPerView: 3,
                    },
                    768: {
                        slidesPerView: 3,
                    },
                }}
                navigation={{
                    prevEl: '.swiper-button-prev',
                    nextEl: '.swiper-button-next',
                }}
                modules={[Navigation]}
            >
                {data.map((d, index) => (
                    <SwiperSlide key={index} style={slideStyle} onClick={() => handleClick(d.d_name, d.d_id)}>
                        <Image src={d.d_logo} alt={d.d_name} width={300} height={200} style={imageStyle} onError={(e) => { e.currentTarget.src = '/placeholder_Ig.webp'; }}/>
                    </SwiperSlide>

                ))}
            </Swiper>
        </div>
    );
};

export default ImageSlider;